import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Z_DIALOGS } from '../../constants/zIndex';
import type { ZIndexContextValue, ZIndexProviderProps } from './types';

const BASE_Z_INDEX = Z_DIALOGS.BASE;

export const ZIndexContext = createContext<ZIndexContextValue | null>(null);

export function ZIndexProvider({ children }: ZIndexProviderProps) {
  const [focusStack, setFocusStack] = useState<string[]>([]);

  const registerPanel = useCallback((id: string) => {
    setFocusStack(prev =>
      prev.includes(id) ? [...prev.filter(p => p !== id), id] : [...prev, id]
    );
  }, []);

  const unregisterPanel = useCallback((id: string) => {
    setFocusStack(prev => prev.filter(p => p !== id));
  }, []);

  const bringToFront = useCallback((id: string) => {
    setFocusStack(prev =>
      prev[prev.length - 1] === id ? prev : [...prev.filter(p => p !== id), id]
    );
  }, []);

  const getZIndex = useCallback((id: string): number => {
    const position = focusStack.indexOf(id);
    return position === -1 ? BASE_Z_INDEX : BASE_Z_INDEX + position;
  }, [focusStack]);

  const value = useMemo(() => ({
    registerPanel, unregisterPanel, bringToFront, getZIndex,
  }), [registerPanel, unregisterPanel, bringToFront, getZIndex]);

  return <ZIndexContext.Provider value={value}>{children}</ZIndexContext.Provider>;
}
